package com.kinoshazam.screens.main.compact

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyHorizontalGrid
import androidx.compose.foundation.lazy.grid.items
import androidx.compose.foundation.lazy.grid.rememberLazyGridState
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import com.kinoshazam.uiKit.components.cells.cellItem.CellItem
import com.kinoshazam.uiKit.components.cells.cellItem.CellItemState
import com.kinoshazam.uiKit.components.genreItem.CompactGenreItem
import com.kinoshazam.uiKit.components.genreItem.GenreItemState
import com.kinoshazam.uiKit.components.textLabel.TextLabelState
import com.kinoshazam.uiKit.components.toDrawableResource
import com.kinoshazam.uiKit.components.localizedGenreName
import com.kinoshazam.uiKit.components.videoItem.CompactVideoItem
import com.kinoshazam.uiKit.components.videoItem.compactBannerSkeletons
import com.kinoshazam.uiKit.components.videoItem.VideoState
import com.kinoshazam.uiKit.theme.BoldMedium
import com.kinoshazam.uiKit.theme.UiKitTheme
import kinoshazam.resources.generated.resources.Res
import kinoshazam.resources.generated.resources.arrow_right
import kinoshazam.resources.generated.resources.movie
import kinoshazam.resources.generated.resources.new_tv_show
import kinoshazam.resources.generated.resources.tv_shows
import kinoshazam.resources.generated.resources.watch_all
import kinoshazam.resources.generated.resources.which_watch
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import presentation.screens.allVideos.AllVideosState.ScreenType.NewTvShows
import presentation.screens.main.MainMVIAction
import presentation.screens.main.MainState

@Composable
fun TvShowsContent(
    paddingValues: PaddingValues,
    state: MainState,
    onAction: (MainMVIAction) -> Unit,
) {
    val scrollState = rememberLazyGridState()
    val fetchNextPage: Boolean by remember {
        derivedStateOf {
            val currentVideoCount = scrollState.layoutInfo.totalItemsCount - 1
            val lastVisibleIndex =
                scrollState.layoutInfo.visibleItemsInfo.lastOrNull()?.index
                    ?: return@derivedStateOf false
            return@derivedStateOf lastVisibleIndex >= currentVideoCount - 5
        }
    }

    LaunchedEffect(key1 = fetchNextPage) {
        if (fetchNextPage) {
            onAction(MainMVIAction.FetchNextPage)
        }
    }

    val tvShows = stringResource(Res.string.tv_shows)
    LazyColumn(
        modifier = Modifier.padding(paddingValues)
    ) {
        item {
            LazyHorizontalGrid(
                modifier = Modifier.height(200.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp),
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                rows = GridCells.FixedSize(96.dp),
                contentPadding = PaddingValues(
                    start = 16.dp,
                    end = 16.dp,
                ),
            ) {
                items(state.genres) { genre ->
                    val name = localizedGenreName(genre.name, genre.icon)
                    CompactGenreItem(
                        state = GenreItemState(
                            id = genre.id ?: -1,
                            title = name,
                            icon = painterResource(
                                genre.icon?.toDrawableResource() ?: Res.drawable.movie
                            )
                        ),
                        onAction = {
                            onAction(
                                MainMVIAction.OnAllVideoClicked(
                                    screenType = NewTvShows,
                                    title = "$tvShows: $name",
                                    genre = genre.id.toString()
                                )
                            )
                        }
                    )
                }
            }
        }

        item {
            CellItem(
                state = CellItemState(
                    title = TextLabelState(
                        text = buildAnnotatedString {
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                    fontWeight = BoldMedium.fontWeight
                                )
                            ) {
                                append("${stringResource(Res.string.tv_shows)},")
                            }
                            withStyle(
                                style = SpanStyle(
                                    color = UiKitTheme.colors.color_2,
                                )
                            ) {
                                append(" ${stringResource(Res.string.which_watch)}")
                            }
                        }
                    )
                )
            )

            val newTvShow = stringResource(Res.string.new_tv_show)
            val shows = state.newTvShows
            LazyRow(
                horizontalArrangement = Arrangement.spacedBy(4.dp),
                contentPadding = PaddingValues(start = 16.dp, end = 16.dp)
            ) {
                if (shows == null) {
                    compactBannerSkeletons()
                } else itemsIndexed(shows) { index, video ->
                    CompactVideoItem(
                        state = VideoState(
                            id = video.id ?: -1,
                            title = video.title.orEmpty(),
                            image = video.imageUrl,
                            placeHolder = painterResource(Res.drawable.movie),
                            placeHolderColor = UiKitTheme.colors.color_5,
                            onAction = { id ->
                                if (video.isTvShow) {
                                    onAction(MainMVIAction.OnTvShowClicked(id))
                                } else {
                                    onAction(MainMVIAction.OnTvShowClicked(id))
                                }
                            }
                        ),
                    )
                }
                item {
                    CompactVideoItem(
                        state = VideoState(
                            id = 1,
                            title = stringResource(Res.string.watch_all),
                            placeHolder = painterResource(Res.drawable.arrow_right),
                            placeHolderColor = UiKitTheme.colors.color_2,
                            onAction = {
                                onAction(
                                    MainMVIAction.OnAllVideoClicked(
                                        screenType = NewTvShows,
                                        title = newTvShow,
                                    )
                                )
                            }
                        ),
                    )
                }
            }
        }
    }
}